<script setup lang="ts">
  import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
  import Breadcrumb from '@/components/ui/Breadcrumb.vue'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import NavbarActions from '@/components/core/NavbarActions.vue'
  import { useAppStore } from '@/stores/app'
  import { useAuthStore } from '@/stores/auth'
  import { Icon } from '@iconify/vue'
  import { Menu } from 'lucide-vue-next'
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import router from '@/plugins/router'
  import cartoonAvatar from '@/assets/images/cartoon-avatar.avif'

  const store = useAppStore()
  const { t } = useI18n()
  const authStore = useAuthStore()
  const userName = computed(
    () => authStore.currentUser?.name || authStore.currentUser?.email?.split('@')[0] || 'Admin'
  )
  const userRole = computed(() => authStore.currentUser?.roleName || 'Administrator')
  const userInitials = computed(() => {
    const name = authStore.currentUser?.name?.trim()
    if (!name) return ''
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase()
  })

  const logoutDialogOpen = ref(false)
  const loggingOut = ref(false)

  const openLogoutDialog = () => {
    logoutDialogOpen.value = true
  }

  const handleLogout = async () => {
    if (loggingOut.value) return
    loggingOut.value = true
    try {
      await authStore.logout()
      logoutDialogOpen.value = false
      router.push('/login')
    } finally {
      loggingOut.value = false
    }
  }
</script>

<template>
  <nav class="top-0 start-0 z-40">
    <div
      class="h-full w-full flex items-center justify-between py-2 px-4 md:px-6 lg:px-8 bg-bg-primary border-b border-stroke-tertiary"
    >
      <!-- Mobile Menu Button -->
      <Button variant="ghost" size="icon" class="lg:hidden" @click="store.toggleSidebar()">
        <Menu class="h-5 w-5" />
      </Button>

      <!-- Breadcrumb -->
      <div class="hidden md:block">
        <Breadcrumb />
      </div>

      <!-- Spacer for mobile -->
      <div class="md:hidden"></div>

      <!-- User Menu -->
      <div class="flex items-center gap-4">
        <NavbarActions />
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              variant="ghost"
              class="group h-auto rounded-2xl py-1 my-2 hover:bg-muted/60 data-[state=open]:bg-muted/70"
            >
              <div class="flex min-w-0 items-center gap-3">
                <Avatar class="h-10 w-10 bg-muted">
                  <AvatarImage :src="cartoonAvatar" :alt="userName" />
                  <AvatarFallback class="bg-muted text-muted-foreground text-sm font-medium">
                    <span v-if="userInitials">{{ userInitials }}</span>
                    <Icon v-else icon="solar:user-linear" class="size-6" />
                  </AvatarFallback>
                </Avatar>
                <span class="flex min-w-0 flex-col items-start text-start">
                  <p class="max-w-36 truncate text-sm font-medium">{{ userName }}</p>
                  <small class="max-w-36 truncate text-xs text-muted-foreground">{{
                    userRole
                  }}</small>
                </span>
                <Icon
                  icon="lucide:chevron-down"
                  class="size-4 text-muted-foreground ms-1 transition-transform duration-200 group-data-[state=open]:rotate-180"
                />
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent class="w-64 rounded-xl border-stroke-tertiary p-2">
            <div class="px-2 py-1.5">
              <p class="text-sm font-medium text-shade-primary">{{ userName }}</p>
              <p class="text-xs text-muted-foreground">{{ userRole }}</p>
            </div>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              class="cursor-pointer rounded-lg px-2.5 py-2 text-additional-red-primary data-[highlighted]:bg-red-500/10 data-[highlighted]:text-additional-red-primary focus:bg-red-500/10 focus:text-additional-red-primary"
              @click="openLogoutDialog"
            >
              <Icon icon="solar:logout-2-outline" class="size-4 me-2" />
              <span>{{ t('auth.logout') }}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
    <Dialog v-model:open="logoutDialogOpen">
      <DialogContent class="sm:max-w-md p-6" hide-close>
        <DialogHeader>
          <DialogTitle>{{ t('auth.logout_confirm_title') }}</DialogTitle>
          <DialogDescription>{{ t('auth.logout_confirm_message') }}</DialogDescription>
        </DialogHeader>
        <DialogFooter class="pt-4 flex flex-row gap-3">
          <Button
            variant="outline"
            class="flex-1"
            :disabled="loggingOut"
            @click="logoutDialogOpen = false"
          >
            {{ t('buttons.cancel') }}
          </Button>
          <Button variant="destructive" class="flex-1" :disabled="loggingOut" @click="handleLogout">
            <Icon v-if="loggingOut" icon="lucide:loader-circle" class="size-4 animate-spin" />
            <span>{{ t('auth.logout') }}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </nav>
</template>
